import React from 'react';
import Icon from '../AppIcon';

const LoadingStateManager = ({ 
  type = 'spinner',
  size = 'default',
  message = 'Loading...',
  fullScreen = false,
  overlay = false
}) => {
  const sizeClasses = {
    sm: 'w-4 h-4',
    default: 'w-8 h-8',
    lg: 'w-12 h-12',
    xl: 'w-16 h-16'
  };

  const iconSizes = {
    sm: 16,
    default: 32,
    lg: 48,
    xl: 64
  };

  const renderSpinner = () => (
    <div className="flex flex-col items-center justify-center gap-4">
      <div className={`${sizeClasses?.[size]} animate-spin`}>
        <Icon 
          name="Loader2" 
          size={iconSizes?.[size]} 
          color="var(--color-primary)" 
        />
      </div>
      {message && (
        <p className="text-sm text-muted-foreground font-medium">
          {message}
        </p>
      )}
    </div>
  );

  const renderSkeleton = () => (
    <div className="space-y-4 w-full">
      <div className="h-8 bg-muted rounded-md animate-pulse-subtle" />
      <div className="h-4 bg-muted rounded-md animate-pulse-subtle w-3/4" />
      <div className="h-4 bg-muted rounded-md animate-pulse-subtle w-1/2" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
        <div className="h-32 bg-muted rounded-md animate-pulse-subtle" />
        <div className="h-32 bg-muted rounded-md animate-pulse-subtle" />
      </div>
    </div>
  );

  const renderProgress = () => (
    <div className="flex flex-col items-center justify-center gap-4 w-full max-w-md">
      <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
        <div 
          className="h-full bg-primary rounded-full transition-smooth"
          style={{ 
            width: '60%',
            animation: 'progress 2s ease-in-out infinite'
          }}
        />
      </div>
      {message && (
        <p className="text-sm text-muted-foreground font-medium">
          {message}
        </p>
      )}
    </div>
  );

  const renderInline = () => (
    <div className="flex items-center gap-2">
      <div className={`${sizeClasses?.sm} animate-spin`}>
        <Icon 
          name="Loader2" 
          size={iconSizes?.sm} 
          color="var(--color-primary)" 
        />
      </div>
      {message && (
        <span className="text-sm text-muted-foreground">
          {message}
        </span>
      )}
    </div>
  );

  const renderContent = () => {
    switch (type) {
      case 'skeleton':
        return renderSkeleton();
      case 'progress':
        return renderProgress();
      case 'inline':
        return renderInline();
      default:
        return renderSpinner();
    }
  };

  if (fullScreen) {
    return (
      <div className="fixed inset-0 bg-background z-[1030] flex items-center justify-center">
        {renderContent()}
      </div>
    );
  }

  if (overlay) {
    return (
      <div className="absolute inset-0 bg-background/80 z-[1030] flex items-center justify-center rounded-md">
        {renderContent()}
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center p-8">
      {renderContent()}
    </div>
  );
};

export default LoadingStateManager;